/*
 * Purchase Requests — Phase 39a.
 *
 * Storekeeper raises a PR when a material is short → Engineer approves →
 *   • Enterprise plan (or SuperAdmin-granted purchase_module): auto/one-click
 *     conversion into a draft LPO in the existing Purchase Module. Procurement
 *     Officer attaches 3 supplier quotes, issues the LPO, receives GRN, and
 *     stock is topped up.
 *   • Basic/Pro plan: PR ends here — the storekeeper can print a "Buy list"
 *     PDF to hand to a walk-in supplier, or email it to their manager.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BreadcrumbFromOrigin from '../components/BreadcrumbFromOrigin';
import { AuthContext, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, X, ShoppingCart, Clock, TrendingUp, Loader2, Printer, Mail, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const APPROVER_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'];
const PROC_ROLES = ['PROCUREMENT_OFFICER', 'ADMIN', 'FULL_ACCESS', 'CEO', 'SUPERADMIN'];

const STATUS_LABEL = {
  pending_engineer_approval: '⏳ Awaiting engineer approval',
  engineer_approved: '✓ Approved — pending procurement (LPO)',
  engineer_rejected: '⛔ Rejected',
  purchase_processing: '🛒 With procurement',
  lpo_issued: '📄 LPO issued',
  received: '📦 Goods received',
  issued_and_closed: '✅ Issued & closed',
};

export default function PurchaseRequests() {
  /* eslint-disable react/no-unstable-nested-components */
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPurchaseModule, setHasPurchaseModule] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [prR, featR] = await Promise.all([
        axios.get(`${API}/purchase-requests`, { withCredentials: true }),
        axios.get(`${API}/purchase-requests/features`, { withCredentials: true }),
      ]);
      setRows(prR.data);
      setHasPurchaseModule(!!featR.data?.purchase_module);
    } catch (e) {
      toast.error('Failed to load PRs');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link support:
  //   /purchase-requests?highlight=PR-2026-0001  (by number, used by MR→PR chain)
  //   /purchase-requests?focus=<pr.id>           (by id, used by Approvals eye)
  // Scrolls to the matching row + briefly outlines it (amber 4-ring, 4s)
  // so the engineer arriving from Approvals finds the PR instantly.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const byNumber = searchParams.get('highlight');
    const byId = searchParams.get('focus');
    if ((!byNumber && !byId) || loading) return;
    const row = rows.find(r => (byId && r.id === byId) || (byNumber && r.pr_number === byNumber));
    if (!row) return;
    const el = document.querySelector(`[data-testid="pr-row-${row.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2', 'shadow-lg', 'transition');
    const t = setTimeout(() => {
      el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2', 'shadow-lg');
      if (byId) {
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [searchParams, rows, loading]);

  const act = async (verb, id, payload = {}) => {
    try {
      await axios.put(`${API}/purchase-requests/${id}/${verb}`, payload, { withCredentials: true });
      toast.success(`PR ${verb}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const convertToLpo = async (pr) => {
    setBusyId(pr.id);
    try {
      const r = await axios.post(`${API}/purchase-requests/${pr.id}/convert-to-po`,
        { supplier_name: 'TBD — attach 3 supplier quotes', unit_price: 0 },
        { withCredentials: true });
      toast.success(`LPO ${r.data.po_number} drafted — attach quotes & send`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create LPO');
    } finally { setBusyId(null); }
  };

  const printBuyList = (list) => {
    // Generate a printable HTML "Buy List" and open in new tab (uses browser print → PDF)
    const w = window.open('', '_blank');
    if (!w) return toast.error('Please allow pop-ups');
    const items = list.map(pr => `
      <tr>
        <td>${pr.pr_number || ''}</td>
        <td>${escapeHtml(pr.item || '')}</td>
        <td style="text-align:right">${pr.stock_shortfall ?? pr.qty ?? ''}</td>
        <td>${pr.unit || ''}</td>
        <td>${escapeHtml(pr.brand || '')}</td>
        <td>${escapeHtml(pr.size || '')}</td>
        <td>${escapeHtml(pr.complaint_ticket_number || '')}</td>
      </tr>`).join('');
    w.document.write(`
      <html><head><title>Buy List</title>
      <style>
        body{font-family:Arial;margin:24px;color:#111}
        h1{margin:0 0 4px 0}
        .sub{color:#666;margin-bottom:16px;font-size:12px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
        th{background:#eef;text-align:left}
      </style></head><body>
      <h1>Materials to Purchase</h1>
      <div class="sub">Generated ${new Date().toLocaleString()} · Approved Purchase Requests</div>
      <table>
        <thead><tr><th>PR #</th><th>Item</th><th>Qty short</th><th>Unit</th><th>Brand</th><th>Size</th><th>Ticket</th></tr></thead>
        <tbody>${items || '<tr><td colspan="7" style="text-align:center">No items</td></tr>'}</tbody>
      </table>
      <script>window.onload=()=>window.print();</script>
      </body></html>`);
    w.document.close();
  };

  const emailManager = async (list) => {
    if (!list.length) return toast.info('Nothing to email');
    const subject = `Materials to purchase (${list.length} items)`;
    const body =
      `Please arrange the purchase of the following items:\n\n` +
      list.map(pr => `• ${pr.pr_number} — ${pr.item} × ${pr.stock_shortfall ?? pr.qty} ${pr.unit || ''}` +
        (pr.brand ? ` · Brand: ${pr.brand}` : '') +
        (pr.size ? ` · Size: ${pr.size}` : '') +
        (pr.complaint_ticket_number ? ` · Ticket: ${pr.complaint_ticket_number}` : '')).join('\n') +
      `\n\nRaised by: ${user?.name || ''}\nCompany: ${user?.company_name || ''}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const canApprove = APPROVER_ROLES.includes(user?.role);
  const isProc = PROC_ROLES.includes(user?.role);
  const isStorekeeper = user?.role === 'STOREKEEPER';

  const pending = rows.filter(r => r.status === 'pending_engineer_approval');
  const approvedNoPo = rows.filter(r => r.status === 'engineer_approved');
  const inFlight = rows.filter(r => ['purchase_processing', 'lpo_issued'].includes(r.status));
  const closed  = rows.filter(r => ['received', 'issued_and_closed', 'engineer_rejected'].includes(r.status));
  const mine = rows.filter(r => r.requested_by === user?.id);

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div></Layout>;

  return (
    <Layout>
      <div data-testid="purchase-requests-page" className="space-y-6">
        <BreadcrumbFromOrigin sourceLabel="Material Request" sourceHref="/material-requests" />
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-purple-500" />Purchase Requests</h1>
          <p className="text-sm text-muted-foreground">
            Auto-raised by storekeeper when stock is short. Flow: Engineer approves → {hasPurchaseModule ? 'Procurement issues LPO → GRN → Stock updated → Issued to job.' : 'Print buy list / email manager → Purchase manually → Add to stock → Issue to job.'}
          </p>
        </div>

        {!hasPurchaseModule && (
          <Card data-testid="pr-upgrade-banner" className="p-4 border-l-4 border-amber-500 bg-amber-50">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">Unlock LPO, supplier quotes &amp; GRN with the Enterprise plan</div>
                <p className="text-sm text-amber-800 mt-0.5">
                  Convert approved Purchase Requests into a full LPO with three supplier quotes attached,
                  track partial deliveries, and auto-update stock on receipt.
                  <a href="/subscription" className="ml-1 underline font-semibold">See plans →</a>
                </p>
              </div>
            </div>
          </Card>
        )}

        {isStorekeeper && (
          <Section testId="pr-mine" icon={<TrendingUp className="w-5 h-5" />} title="My purchase requests" count={mine.length} rows={mine} />
        )}
        {canApprove && (
          <Section testId="pr-pending" icon={<Clock className="w-5 h-5" />} title="Awaiting your approval" count={pending.length} rows={pending}
            actions={(pr) => (
              <>
                <Button data-testid={`pr-approve-${pr.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act('approve', pr.id)}>
                  <Check className="w-3.5 h-3.5 mr-1" />Approve
                </Button>
                <Button data-testid={`pr-reject-${pr.id}`} size="sm" variant="outline" onClick={() => {
                  const n = window.prompt('Rejection reason:');
                  if (n !== null) act('reject', pr.id, { notes: n });
                }}><X className="w-3.5 h-3.5 mr-1" />Reject</Button>
              </>
            )} />
        )}

        {(isProc || canApprove) && (
          <Section testId="pr-approved" icon={<ShoppingCart className="w-5 h-5" />} title="Approved — ready for procurement" count={approvedNoPo.length} rows={approvedNoPo}
            actions={(pr) => (
              <>
                {hasPurchaseModule ? (
                  <Button data-testid={`pr-to-lpo-${pr.id}`} size="sm" className="bg-purple-600 hover:bg-purple-700"
                          disabled={busyId === pr.id}
                          onClick={() => convertToLpo(pr)}>
                    {busyId === pr.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
                    Convert to LPO
                  </Button>
                ) : null}
              </>
            )}>
            {!hasPurchaseModule && approvedNoPo.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
                <Button data-testid="pr-print-buylist" size="sm" variant="outline" onClick={() => printBuyList(approvedNoPo)}>
                  <Printer className="w-3.5 h-3.5 mr-1" />Print buy list
                </Button>
                <Button data-testid="pr-email-manager" size="sm" variant="outline" onClick={() => emailManager(approvedNoPo)}>
                  <Mail className="w-3.5 h-3.5 mr-1" />Email manager
                </Button>
                <span className="text-[11px] text-slate-500 ml-auto self-center">Enterprise plan unlocks 1-click LPO with 3 supplier quotes + GRN.</span>
              </div>
            )}
          </Section>
        )}

        {(isProc || canApprove) && (
          <Section testId="pr-inflight" icon={<ShoppingCart className="w-5 h-5" />} title="In procurement pipeline" count={inFlight.length} rows={inFlight} />
        )}
        <Section testId="pr-closed" icon={<Check className="w-5 h-5" />} title="Completed / Rejected" count={closed.length} rows={closed} />
      </div>
    </Layout>
  );
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
}

function Section({ testId, icon, title, count, rows, actions, children }) {
  return (
    <Card data-testid={testId} className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-purple-600">{icon}</div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold">{count}</span>
      </div>
      {count === 0 ? <p className="text-sm text-slate-500 italic">Nothing here right now.</p> : (
        <div className="space-y-2">
          {rows.map(pr => (
            <div key={pr.id} data-testid={`pr-row-${pr.id}`} className="p-3 bg-white rounded border">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-[10px] px-1.5 py-0.5 rounded bg-purple-700 text-white font-mono">{pr.pr_number}</code>
                {pr.complaint_ticket_number && <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-amber-200 font-mono">{pr.complaint_ticket_number}</code>}
                {pr.linked_po_number && (
                  <a href={`/purchase-orders?highlight=${encodeURIComponent(pr.linked_po_number)}&from=${encodeURIComponent(pr.pr_number)}`} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-700 text-white font-mono hover:bg-emerald-800">
                    → {pr.linked_po_number}
                  </a>
                )}
                <span className="font-semibold text-sm">{pr.item}</span>
                <span className="text-xs text-slate-500">× {pr.qty} {pr.unit}</span>
                <span className="ml-auto text-[11px] font-semibold text-slate-600">{STATUS_LABEL[pr.status] || pr.status}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Raised by {pr.requested_by_name} · Stock short by {pr.stock_shortfall}
                {pr.is_returnable && ' · 🔄 Returnable'}
                {pr.rejection_notes && ` · Reason: ${pr.rejection_notes}`}
              </div>
              {actions && (
                <div className="mt-2 flex gap-2">{actions(pr)}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {children}
    </Card>
  );
}
